import React, {useCallback, useEffect, useRef} from "react";
import Panzoom from "@panzoom/panzoom/dist/panzoom.js";

export const MINIMUM_VIEWPORT_SPAN_MULTIPLIER = 2;
export const DEFAULT_MAXIMUM_ZOOM_MULTIPLIER = 2.5;

const ZOOM_STEP = 0.5;

export const getMinimumZoomScale = (stageWidth, stageHeight, imageWidth, imageHeight) => {
    if (stageWidth >= stageHeight) {
        return (stageWidth * MINIMUM_VIEWPORT_SPAN_MULTIPLIER) / imageWidth;
    }

    return (stageHeight * MINIMUM_VIEWPORT_SPAN_MULTIPLIER) / imageHeight;
};

export const getInitialPanPosition = (stageWidth, stageHeight, imageWidth, imageHeight, scale) => ({
    x: (stageWidth - imageWidth) / (2 * scale),
    y: (stageHeight - imageHeight) / (2 * scale),
});

const preventDefault = (event) => {
    event.preventDefault();
};

const ProtectedImageViewer = ({
    imageUrl,
    imageName,
    onClose,
    maximumZoomMultiplier = DEFAULT_MAXIMUM_ZOOM_MULTIPLIER,
}) => {
    const stageRef = useRef(null);
    const imageRef = useRef(null);
    const panzoomRef = useRef(null);
    const wheelHandlerRef = useRef(null);
    const containmentTimerRef = useRef(null);

    const destroyPanzoom = useCallback(() => {
        const stage = stageRef.current;
        const panzoom = panzoomRef.current;

        if (containmentTimerRef.current) {
            window.clearTimeout(containmentTimerRef.current);
        }
        containmentTimerRef.current = null;

        if (stage && wheelHandlerRef.current) {
            stage.removeEventListener("wheel", wheelHandlerRef.current);
        }
        wheelHandlerRef.current = null;

        if (panzoom) {
            panzoom.destroy();
        }
        panzoomRef.current = null;
    }, []);

    const fitImageToStage = useCallback(() => {
        const stage = stageRef.current;
        const image = imageRef.current;

        if (!stage || !image || !image.naturalWidth || !image.naturalHeight) {
            return false;
        }

        const scale = Math.min(
            stage.clientWidth / image.naturalWidth,
            stage.clientHeight / image.naturalHeight,
        );

        if (!scale || !Number.isFinite(scale)) {
            return false;
        }

        const width = Math.round(image.naturalWidth * scale);
        const height = Math.round(image.naturalHeight * scale);

        image.style.width = `${width}px`;
        image.style.height = `${height}px`;
        return {width, height};
    }, []);

    const enableContainmentAfterInitialPosition = useCallback((panzoom) => {
        if (containmentTimerRef.current) {
            window.clearTimeout(containmentTimerRef.current);
        }

        containmentTimerRef.current = window.setTimeout(() => {
            if (panzoomRef.current === panzoom) {
                panzoom.setOptions({contain: "outside"});
            }
        });
    }, []);

    const initializePanzoom = useCallback(() => {
        const stage = stageRef.current;
        const image = imageRef.current;

        if (!stage || !image) {
            return;
        }

        const fittedImage = fitImageToStage();
        if (!fittedImage) {
            return;
        }

        destroyPanzoom();

        const minimumZoomScale = getMinimumZoomScale(
            stage.clientWidth,
            stage.clientHeight,
            fittedImage.width,
            fittedImage.height,
        );
        const startPosition = getInitialPanPosition(
            stage.clientWidth,
            stage.clientHeight,
            fittedImage.width,
            fittedImage.height,
            minimumZoomScale,
        );
        const panzoom = Panzoom(image, {
            canvas: true,
            maxScale: minimumZoomScale * maximumZoomMultiplier,
            minScale: minimumZoomScale,
            startX: startPosition.x,
            startY: startPosition.y,
            startScale: minimumZoomScale,
            step: ZOOM_STEP,
        });
        const wheelHandler = (event) => {
            event.preventDefault();
            panzoom.zoomWithWheel(event);
        };

        stage.addEventListener("wheel", wheelHandler, {passive: false});
        panzoomRef.current = panzoom;
        wheelHandlerRef.current = wheelHandler;
        enableContainmentAfterInitialPosition(panzoom);
    }, [destroyPanzoom, enableContainmentAfterInitialPosition, fitImageToStage, maximumZoomMultiplier]);

    useEffect(() => {
        const image = imageRef.current;

        if (image && image.complete) {
            initializePanzoom();
        }

        return destroyPanzoom;
    }, [destroyPanzoom, imageUrl, initializePanzoom]);

    useEffect(() => {
        const resetForViewportChange = () => {
            const stage = stageRef.current;
            const fittedImage = fitImageToStage();

            if (!panzoomRef.current || !stage || !fittedImage) {
                return;
            }

            const minimumZoomScale = getMinimumZoomScale(
                stage.clientWidth,
                stage.clientHeight,
                fittedImage.width,
                fittedImage.height,
            );
            const startPosition = getInitialPanPosition(
                stage.clientWidth,
                stage.clientHeight,
                fittedImage.width,
                fittedImage.height,
                minimumZoomScale,
            );
            panzoomRef.current.setOptions({
                contain: false,
                maxScale: minimumZoomScale * maximumZoomMultiplier,
                minScale: minimumZoomScale,
                startX: startPosition.x,
                startY: startPosition.y,
                startScale: minimumZoomScale,
            });
            panzoomRef.current.reset({animate: false});
            enableContainmentAfterInitialPosition(panzoomRef.current);
        };

        window.addEventListener("resize", resetForViewportChange);
        window.addEventListener("orientationchange", resetForViewportChange);

        return () => {
            window.removeEventListener("resize", resetForViewportChange);
            window.removeEventListener("orientationchange", resetForViewportChange);
        };
    }, [enableContainmentAfterInitialPosition, fitImageToStage, maximumZoomMultiplier]);

    useEffect(() => {
        const closeOnEscape = (event) => {
            if (event.key === "Escape") {
                event.preventDefault();
                onClose();
            }
        };

        window.addEventListener("keydown", closeOnEscape);
        return () => {
            window.removeEventListener("keydown", closeOnEscape);
        };
    }, [onClose]);

    if (!imageUrl) {
        return null;
    }

    const zoomIn = () => {
        if (panzoomRef.current) {
            panzoomRef.current.zoomIn();
        }
    };

    const zoomOut = () => {
        if (panzoomRef.current) {
            panzoomRef.current.zoomOut();
        }
    };

    const resetZoom = () => {
        if (panzoomRef.current) {
            panzoomRef.current.reset();
        }
    };

    return (
        <div
            className="customer-image-viewer"
            onContextMenu={preventDefault}
            role="dialog"
            aria-modal="true"
            aria-label={imageName ? `${imageName} képnézegető` : "Képnézegető"}
        >
            <div
                className="customer-image-viewer-stage"
                ref={stageRef}
                onContextMenu={preventDefault}
            >
                <img
                    className="customer-image-viewer-image"
                    ref={imageRef}
                    src={imageUrl}
                    alt={imageName || ""}
                    draggable={false}
                    onDragStart={preventDefault}
                    onLoad={initializePanzoom}
                />
            </div>

            <button
                type="button"
                onClick={onClose}
                className="customer-image-viewer-back btn btn-simple btn-info btn-icon"
                aria-label="Vissza a galériához"
            >
                <i className="material-icons material-icons--outlined">navigate_before</i>
            </button>
            <div className="customer-image-viewer-controls" role="group" aria-label="Nagyítás vezérlők">
                <button
                    type="button"
                    onClick={zoomOut}
                    className="btn btn-simple btn-info btn-icon"
                    aria-label="Kicsinyítés"
                >
                    <i className="material-icons material-icons--outlined">remove</i>
                </button>
                <button
                    type="button"
                    onClick={resetZoom}
                    className="btn btn-simple btn-info btn-icon"
                    aria-label="Nagyítás visszaállítása a minimális értékre"
                >
                    <i className="material-icons material-icons--outlined">center_focus_strong</i>
                </button>
                <button
                    type="button"
                    onClick={zoomIn}
                    className="btn btn-simple btn-info btn-icon"
                    aria-label="Nagyítás"
                >
                    <i className="material-icons material-icons--outlined">add</i>
                </button>
            </div>
            <button
                type="button"
                onClick={onClose}
                className="customer-image-viewer-close btn btn-simple btn-danger btn-icon"
                aria-label="Képnézegető bezárása"
            >
                <i className="material-icons material-icons--outlined">close</i>
            </button>
        </div>
    );
};

export default ProtectedImageViewer;